import { z } from 'zod';

import {
  creditCardCvcStrategy,
  creditCardExpireDateStrategy,
  creditCardNumberStrategy,
  emailFormatStrategy,
  requiredStrategy,
} from '@autopass-web/mochi-utils';

import { DEFAULT_SUPPORTED_PAYMENTS } from '../../configs';
import { checkIfApplePaySupported } from '../../utils/check-if-apple-pay-supported';

import type { ChainStoreInvoice, ReceiptSet } from '../../types';
import type { PaymentMethodType } from '@autopass-web/types';

const EASY_WALLET_UPPER_LIMIT = 30000;

export const DEFAULT_RECEIPT_SET = {
  WITH_EMAIL: {
    type: 'carrier_member',
    value: null,
  },
  WITHOUT_EMAIL: {
    type: 'donate',
    value: '88888',
  },
  PAYMENT_CARRIER: {
    type: 'payment_carrier',
    value: '',
  },
} satisfies Record<string, ReceiptSet>;

export const creditCardSchema = z.object({
  number: z
    .string()
    .refine(requiredStrategy, {
      message: '此欄位為必填',
    })
    .refine(creditCardNumberStrategy, {
      message: '格式錯誤',
    }),
  exp: z
    .string()
    .refine(requiredStrategy, {
      message: '此欄位為必填',
    })
    .refine(creditCardExpireDateStrategy, {
      message: '格式錯誤',
    }),
  cvc: z
    .string()
    .refine(requiredStrategy, {
      message: '此欄位為必填',
    })
    .refine(creditCardCvcStrategy, {
      message: '格式錯誤',
    }),
});

export const emailSchema = z
  .string()
  .or(z.null())
  .refine(
    (value) => {
      return value !== '';
    },
    {
      message: '此欄位為必填',
    },
  )
  .refine(
    (value) => {
      return value === null ? true : emailFormatStrategy(value);
    },
    {
      message: '格式錯誤',
    },
  );

export const getSchema = (
  paymentMethod: PaymentMethodType,
  hasEmail: boolean,
) => {
  const baseSchema = {};

  if (paymentMethod === 'credit_card') {
    baseSchema['creditCard'] = creditCardSchema;
  }

  if (hasEmail) {
    baseSchema['email'] = emailSchema;
  }

  return z.object(baseSchema);
};

export const getSupportPayments = ({
  capture_method,
  amount,
  mochipay_issue_receipt,
  payment_options,
}: Pick<
  ChainStoreInvoice,
  'capture_method' | 'amount' | 'payment_options' | 'mochipay_issue_receipt'
>) => {
  let paymentMethods = DEFAULT_SUPPORTED_PAYMENTS.filter((method) => {
    return payment_options.includes(method);
  });

  const isApplePaySupported = checkIfApplePaySupported();
  if (!isApplePaySupported) {
    paymentMethods = paymentMethods.filter((p) => p !== 'apple_pay');
  }

  if (
    capture_method === 'manual' ||
    parseInt(amount) > EASY_WALLET_UPPER_LIMIT ||
    !mochipay_issue_receipt
  ) {
    paymentMethods = paymentMethods.filter((p) => p !== 'easy_wallet');
  }

  return paymentMethods;
};
